import React from "react";

// Shared pagination footer used by the Payments, Receipts and Manual Payments
// tabs. Mirrors the markup that lived (duplicated) in PendingPaymentsNew /
// PaymentHistory / ManualPaymentsHistory.
//
// Props:
//   pagination: { currentPage, totalPages, totalCount, limit, hasNextPage, hasPrevPage }
//   onPageChange: (page:number) => void
//   onLimitChange: (limit:number) => void  (optional; hides the page-size select when omitted)
//   showFirstLast: boolean (default true)
const Pagination = ({
  pagination,
  onPageChange,
  onLimitChange,
  showFirstLast = true,
}) => {
  const {
    currentPage = 1,
    totalPages = 1,
    totalCount = 0,
    limit = 20,
    hasNextPage,
    hasPrevPage,
  } = pagination || {};

  if (totalPages <= 1) return null;

  // Some source lists provided hasNextPage/hasPrevPage flags; others (manual
  // payments) only had current/total. Fall back to page math when absent.
  const canPrev = hasPrevPage ?? currentPage > 1;
  const canNext = hasNextPage ?? currentPage < totalPages;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-600">
          Showing {(currentPage - 1) * limit + 1} to{" "}
          {Math.min(currentPage * limit, totalCount)} of {totalCount} results
        </div>
        {onLimitChange && (
          <select
            value={limit}
            onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        )}
      </div>
      <div className="flex items-center gap-2">
        {showFirstLast && (
          <button
            onClick={() => onPageChange(1)}
            disabled={!canPrev}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            First
          </button>
        )}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canPrev}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="px-3 py-1.5 text-sm font-medium">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canNext}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
        {showFirstLast && (
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={!canNext}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Last
          </button>
        )}
      </div>
    </div>
  );
};

export default Pagination;
